import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { getAuth } from 'firebase/auth';

export type VideoProvider = 'openai' | 'grok' | 'google';

export interface VideoCreateRequest {
  provider: VideoProvider;
  prompt: string;
  // optional "image-to-video"
  imageBase64?: string;      // raw base64 (no prefix)
  imageMimeType?: string;    // image/png | image/jpeg | ...
  // optional metadata
  aspectRatio?: '16:9' | '9:16' | '1:1';
  seconds?: number;          // requested duration
}

export interface VideoJob {
  id: string;
  provider: VideoProvider;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  createdAt: string;
  updatedAt: string;
  videoUrl?: string;
  thumbnailUrl?: string;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class VideoFireService {
  private readonly baseUrl = environment.videoApiBaseUrl.replace(/\/+$/, '');
  private activeJobsSubject = new BehaviorSubject<Record<string, VideoJob>>({});
  activeJobs$ = this.activeJobsSubject.asObservable();

  constructor(private http: HttpClient) {}

  // ---------- Auth / UID enforcement ----------
  private async getFirebaseIdTokenOrThrow(): Promise<{ uid: string; token: string }> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user?.uid) {
      throw new Error('You must be signed in to generate video.');
    }
    // hard-block "anon" patterns too, even if you had a local anon uid elsewhere
    if (/^anon[-_]/i.test(user.uid) || user.isAnonymous) {
      throw new Error('Anonymous users cannot generate video. Please log in.');
    }
    const token = await user.getIdToken(/* forceRefresh */ true);
    return { uid: user.uid, token };
  }

  private updateJob(job: VideoJob) {
    const current = this.activeJobsSubject.value;
    this.activeJobsSubject.next({ ...current, [job.id]: job });
  }

  async createVideo(req: VideoCreateRequest): Promise<VideoJob> {
    const { token } = await this.getFirebaseIdTokenOrThrow();

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    try {
      const job = await firstValueFrom(
        this.http.post<VideoJob>(`${this.baseUrl}/jobs`, req, { headers })
      );
      this.updateJob(job);
      return job;
    } catch (e) {
      throw this.normalizeHttpError(e);
    }
  }

  async getJob(jobId: string): Promise<VideoJob> {
    const { token } = await this.getFirebaseIdTokenOrThrow();

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    try {
      const job = await firstValueFrom(
        this.http.get<VideoJob>(`${this.baseUrl}/jobs/${encodeURIComponent(jobId)}`, { headers })
      );
      this.updateJob(job);
      return job;
    } catch (e) {
      throw this.normalizeHttpError(e);
    }
  }

  /**
   * Poll until terminal state. Callers can stop polling by aborting externally if desired.
   */
  async pollJob(jobId: string, opts?: { intervalMs?: number; timeoutMs?: number }): Promise<VideoJob> {
    const intervalMs = Math.max(800, opts?.intervalMs ?? 1500);
    const timeoutMs = Math.max(5000, opts?.timeoutMs ?? 5 * 60_000);

    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const job = await this.getJob(jobId);
      if (job.status === 'completed' || job.status === 'failed') return job;
      await new Promise(res => setTimeout(res, intervalMs));
    }
    throw new Error('Video generation timed out.');
  }

  async readFileAsBase64(file: File): Promise<{ base64: string; mimeType: string; dataUrl: string }> {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onerror = () => reject(new Error('Failed to read image.'));
      r.onload = () => resolve(String(r.result || ''));
      r.readAsDataURL(file);
    });

    const match = dataUrl.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,(.*)$/);
    if (!match) throw new Error('Unsupported image format.');
    return { mimeType: match[1], base64: match[2], dataUrl };
  }

  private normalizeHttpError(e: any): Error {
    if (e instanceof HttpErrorResponse) {
      const msg =
        (typeof e.error === 'string' && e.error) ||
        e.error?.message ||
        e.message ||
        `HTTP ${e.status}`;
      return new Error(msg);
    }
    return e instanceof Error ? e : new Error('Unknown error');
  }
}
